import { FormEvent, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { AccountSchema } from "@/domains/openapi/types";
import { api } from "@/modules/api";
import { toastModule } from "@/modules/toast";

import type { Uuid } from "@/domains/global/identifiers";

const PageContainer = styled.div`
  padding: 24px;
  max-width: 720px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Description = styled.p`
  color: var(--gray-12);
  margin-bottom: 24px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--gray-6);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--gray-9);
    box-shadow: 0 0 0 1px var(--gray-9);
  }
`;

const HelperText = styled.div`
  font-size: 13px;
  color: var(--gray-11);
`;

const ErrorText = styled.div`
  color: #b3261e;
`;

const SuccessBox = styled.div`
  border: 1px solid var(--green-9);
  background: var(--green-3);
  padding: 16px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SummaryBox = styled.div`
  border: 1px solid var(--gray-6);
  background: var(--gray-2);
  padding: 16px;
  border-radius: 12px;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

type MigrateAccountEmailAddressRequest = {
  account_id: string;
  new_email_address: string;
  profile_display_name?: string | null;
  profile_photo_url?: string | null;
};

type MigrateAccountEmailAddressResponse = {
  success: boolean;
  account_id: string;
  email_changed: boolean;
};

export const AccountMigrateEmailAddressPage = () => {
  const { accountId } = useParams<{ accountId: Uuid }>();
  const [newEmail, setNewEmail] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<MigrateAccountEmailAddressResponse | null>(null);

  const accountQuery = useQuery<AccountSchema | null>({
    queryKey: ["account-by-id", accountId],
    enabled: Boolean(accountId),
    queryFn: async () => {
      if (!accountId) {
        return null;
      }

      const response = await api.get("/internal/v1/accounts/{account_id}", {
        params: {
          path: {
            account_id: accountId,
          },
        },
      });

      if (response.error) {
        throw buildErrorFromApiResponse(response);
      }

      return response.data ?? null;
    },
  });

  const migrateMutation = useMutation({
    mutationFn: async (payload: MigrateAccountEmailAddressRequest) => {
      const response = await api.post(
        "/internal/v1/utilities/authentications/migrate-account-email-address",
        { body: payload }
      );

      if (response.error || response.response.status !== 200) {
        throw buildErrorFromApiResponse(response as ApiErrorResponse);
      }

      if (!response.data) {
        throw new Error("Failed to migrate email address");
      }

      return response.data as MigrateAccountEmailAddressResponse;
    },
    onSuccess: data => {
      toastModule.triggerToast({ content: "Email migration triggered." });
      setResult(data);
    },
  });

  const canSubmit = useMemo(() => {
    return Boolean(newEmail.trim() && accountId && !migrateMutation.isPending);
  }, [accountId, migrateMutation.isPending, newEmail]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    setResult(null);

    if (!accountId) {
      setSubmitError("Missing account id in route.");
      return;
    }

    if (!newEmail.trim()) {
      setSubmitError("New email address is required.");
      return;
    }

    const reenteredEmail = prompt("Re-enter the new email address to confirm this migration.");

    if (reenteredEmail?.trim() !== newEmail.trim()) {
      setSubmitError("Email migration cancelled – confirmation email did not match.");
      return;
    }

    migrateMutation
      .mutateAsync({
        account_id: accountId,
        new_email_address: newEmail.trim(),
      })
      .catch(error => {
        const message = extractErrorMessage(error, newEmail.trim());
        setSubmitError(message);
      });
  };

  if (!accountId) {
    return (
      <PageContainer>
        <Title>Migrate Account Email</Title>
        <ErrorText>Missing account id in route.</ErrorText>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Migrate Account Email</Title>
      <Description>
        Update an account's primary email address and move any email-based authentications to that
        address. The source account will remain active; soft-deleted duplicates are reassigned
        automatically.
      </Description>

      {accountQuery.isLoading && <HelperText>Loading account details…</HelperText>}
      {accountQuery.isError && (
        <ErrorText>Failed to load account details. Please try again.</ErrorText>
      )}

      {accountQuery.data && (
        <SummaryBox>
          <div>
            <strong>Account:</strong> {accountQuery.data.id}
          </div>
          <div>
            <strong>Display Name:</strong> {accountQuery.data.profile_display_name}
          </div>
          <div>
            <strong>Email: </strong>
            <StyledSpan>{accountQuery.data.profile_email_address}</StyledSpan>
            {newEmail.trim() && (
              <>
                {" → "}
                <strong>{newEmail.trim()}</strong>
              </>
            )}
          </div>
        </SummaryBox>
      )}

      <Form onSubmit={handleSubmit}>
        <FieldGroup>
          <Label htmlFor="new-email">New email address</Label>
          <Input
            id="new-email"
            type="email"
            required
            value={newEmail}
            onChange={event => setNewEmail(event.target.value)}
            placeholder="user@example.com"
          />
        </FieldGroup>

        {submitError && <ErrorText>{submitError}</ErrorText>}
        {migrateMutation.isError && !submitError && (
          <ErrorText>Failed to migrate email address. Please try again.</ErrorText>
        )}

        <Button type="submit" disabled={!canSubmit}>
          {migrateMutation.isPending ? "Migrating…" : "Migrate Email Address"}
        </Button>
      </Form>

      {result && (
        <SuccessBox>
          <div>Email migration request succeeded.</div>
          <div>
            {result.email_changed
              ? "The account email was updated."
              : "The account already used this email."}
          </div>
          <div>
            <Link
              to={
                atlasRoutes.accountsView({
                  params: { accountId: result.account_id },
                }).path
              }
            >
              View account details
            </Link>
          </div>
        </SuccessBox>
      )}
    </PageContainer>
  );
};

const extractErrorMessage = (error: unknown, pendingEmail: string): string => {
  if (typeof error !== "object" || error === null) {
    return DEFAULT_ERROR_MESSAGE;
  }

  const typedError = error as ErrorWithDetail;

  if (typedError.detail) {
    return formatDetail(typedError.detail, pendingEmail);
  }

  if (typedError.message) {
    return typedError.message;
  }

  if ("data" in typedError && typedError.data && typeof typedError.data === "object") {
    const maybeDetail = typedError.data as { detail?: unknown };
    if (typeof maybeDetail.detail === "string") {
      return formatDetail(maybeDetail.detail, pendingEmail);
    }
  }

  return DEFAULT_ERROR_MESSAGE;
};

const DEFAULT_ERROR_MESSAGE =
  "Failed to migrate email address. Ensure no active account already owns the target email.";

const formatDetail = (detail: string, pendingEmail: string): string => {
  if (detail.includes("already uses normalized email")) {
    return `Migration blocked: another active account is already using ${pendingEmail}. Remove or rename that account first.`;
  }

  return detail;
};

type ApiErrorResponse = {
  error?: {
    status?: number;
    detail?: string;
  };
  response?: Response;
};

type ErrorWithDetail = Error & {
  detail?: string | null;
  data?: unknown;
};

const buildErrorFromApiResponse = (apiError: ApiErrorResponse): ErrorWithDetail => {
  let detail: string | null = null;

  if (apiError.error && typeof apiError.error === "object") {
    const maybeDetail = apiError.error as { detail?: unknown };
    if (typeof maybeDetail.detail === "string") {
      detail = maybeDetail.detail;
    }
  }

  const message = detail ?? "Failed to migrate email address";
  const error = new Error(message) as ErrorWithDetail;
  error.detail = detail;
  error.data = apiError.error;
  return error;
};

const StyledSpan = styled.span`
  font-weight: 600;
  text-decoration: underline;
`;
